import { ZButton } from "@/shared/components/button";
import { Modal, ModalContent } from "@nextui-org/react";
import { Check } from "lucide-react";
import type { FC } from "react";

interface WelcomeModalProps {
  /**
   * Whether the modal is open or not
   */
  isOpen: boolean;

  /**
   * Handler called when the modal is closed
   */
  onClose: () => void;

  /**
   * Organization/Assessor name
   */
  organizationName?: string;

  /**
   * Vendor name (optional - can be used in future)
   */
  vendorName?: string;

  /**
   * Product name (optional - to be shown with vendor name)
   */
  productName?: string;

  /**
   * Assessment type/scope
   * @default "Third-Party Risk Assessment"
   */
  assessmentType?: string;

  /**
   * Modal mode - determines content and behavior
   * @default "welcome"
   */
  mode?: 'welcome' | 'help' | 'chat-welcome';

  /**
   * Current progress percentage (0-100)
   * Used to show step completion status
   * @default 0
   */
  progressPercentage?: number;

  /**
   * Current step (1, 2, or 3)
   * Indicates which step user is currently on
   * @default 1
   */
  currentStep?: number;
}

const WelcomeModal: FC<WelcomeModalProps> = ({
  isOpen,
  onClose,
  organizationName = "the organization",
  vendorName,
  productName,
  assessmentType = "Third-Party Risk Assessment",
  mode = 'welcome',
  progressPercentage = 0,
  currentStep = 1,
}) => {
  // Determine content based on mode
  const isHelpMode = mode === 'help';
  const isChatWelcomeMode = mode === 'chat-welcome';

  const title = isChatWelcomeMode
    ? "Automated Review Complete"
    : isHelpMode
    ? "About This Due-Diligence Process"
    : `Welcome to Your Due Diligence Request for ${organizationName}`;

  // Build subtitle: Show vendor_name + product_name if both available, otherwise fall back to assessmentType
  const subtitle = isChatWelcomeMode
    ? "Our AI has analyzed your responses and identified areas for clarification."
    : isHelpMode
    ? null
    : vendorName && productName
      ? `This review covers: ${vendorName} ${productName}`
      : vendorName
      ? `This review covers: ${vendorName}`
      : `This review covers: ${assessmentType}`;

  const introText = isChatWelcomeMode
    ? null
    : isHelpMode
    ? "This process includes three steps. You can return at any time to review your progress or continue from where you left off. Your place in the timeline below reflects your current status."
    : "Please complete required responses and documents; we'll guide you through review and clarifications.";

  const buttonText = isChatWelcomeMode
    ? "View Follow-up Questions"
    : isHelpMode ? "Close" : "Get Started";

  // Calculate step states
  const isStep1Complete = progressPercentage === 100 || currentStep > 1;
  const isStep1Active = currentStep === 1 && progressPercentage < 100;
  const isStep2Complete = currentStep > 2;
  const isStep2Active = currentStep === 2 || (currentStep === 1 && progressPercentage === 100);
  const isStep3Active = currentStep === 3;
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      isDismissable={true}
      size="lg"
    >
      <ModalContent className="max-w-[512px] shadow-[0px_0px_24px_rgba(0,0,0,0.05)] rounded-[16px] p-6">
        {/* Main Container */}
        <div className="relative flex flex-col items-center p-0 w-full">
          {/* Content Section */}
          <div className="flex flex-col items-start gap-6 w-full">
            {/* Title and Description */}
            <div className="flex flex-col items-center gap-2 w-full">
              <h2 className="text-[18px] font-semibold leading-[28px] text-center text-[#11181C] w-full">
                {title}
              </h2>
              {subtitle && (
                <p className="text-[14px] font-medium leading-[20px] text-center text-[#11181C] w-full">
                  {subtitle}
                </p>
              )}
              {introText && (
                <p className="text-[14px] font-normal leading-[20px] text-center text-[#3F3F46] w-full">
                  {introText}
                </p>
              )}
            </div>

            {/* Process Steps Container */}
            <div className="flex flex-col items-start py-5 px-4 gap-3 w-full bg-[#FAFAFA] border border-[#E4E4E7] rounded-lg">
              {/* Step 1 */}
              <div className="flex flex-row items-start gap-4 w-full">
                {/* Icon Column */}
                <div className="flex flex-col items-center gap-3 flex-shrink-0">
                  {/* Circle Indicator */}
                  <div
                    className={`w-5 h-5 rounded-full shadow-[0px_4px_6px_-1px_rgba(0,0,0,0.1),0px_2px_4px_-1px_rgba(0,0,0,0.06)] flex items-center justify-center ${
                      isStep1Complete
                        ? 'bg-[#E8FAF0] border border-[#17C964]'
                        : isStep1Active
                        ? 'bg-[#E6F1FE] border border-[#006FEE]'
                        : 'bg-white border border-[#71717A]'
                    }`}
                  >
                    {isStep1Complete && (
                      <Check className="w-3 h-3 text-[#17C964]" strokeWidth={3} />
                    )}
                  </div>
                  {/* Connecting Line */}
                  <div className={`w-px flex-1 min-h-[44px] ${
                    isStep1Complete ? 'bg-[#17C964]' : 'bg-[#71717A]'
                  }`} />
                </div>
                {/* Content Column */}
                <div className="flex flex-col items-start pb-3 gap-1 flex-1">
                  <p className="text-[14px] font-medium leading-[20px] text-[#11181C]">
                    Complete Mandatory Requirements
                  </p>
                  <p className="text-[14px] font-normal leading-[20px] text-[#71717A]">
                    Respond to the questions and upload documents requested.
                  </p>
                </div>
              </div>

              {/* Step 2 */}
              <div className="flex flex-row items-start gap-4 w-full">
                {/* Icon Column */}
                <div className="flex flex-col items-center gap-3 flex-shrink-0">
                  {/* Circle Indicator */}
                  <div
                    className={`w-5 h-5 rounded-full shadow-[0px_4px_6px_-1px_rgba(0,0,0,0.1),0px_2px_4px_-1px_rgba(0,0,0,0.06)] flex items-center justify-center ${
                      isStep2Complete
                        ? 'bg-[#E8FAF0] border border-[#17C964]'
                        : isStep2Active
                        ? 'bg-[#E6F1FE] border border-[#006FEE]'
                        : 'bg-white border border-[#71717A]'
                    }`}
                  >
                    {isStep2Complete && (
                      <Check className="w-3 h-3 text-[#17C964]" strokeWidth={3} />
                    )}
                  </div>
                  {/* Connecting Line */}
                  <div className={`w-px flex-1 min-h-[44px] ${
                    isStep2Complete ? 'bg-[#17C964]' : 'bg-[#71717A]'
                  }`} />
                </div>
                {/* Content Column */}
                <div className="flex flex-col items-start pb-3 gap-1 flex-1">
                  <p className="text-[14px] font-medium leading-[20px] text-[#11181C]">
                    Proceed to Automated Review
                  </p>
                  <p className="text-[14px] font-normal leading-[20px] text-[#71717A]">
                    AI will evaluate your response to generate follow-up questions.
                  </p>
                </div>
              </div>

              {/* Step 3 */}
              <div className="flex flex-row items-start gap-4 w-full">
                {/* Icon Column */}
                <div className="flex flex-col items-center flex-shrink-0">
                  {/* Circle Indicator */}
                  <div className={`w-5 h-5 rounded-full shadow-[0px_4px_6px_-1px_rgba(0,0,0,0.1),0px_2px_4px_-1px_rgba(0,0,0,0.06)] ${
                    isStep3Active
                      ? 'bg-[#E6F1FE] border border-[#006FEE]'
                      : 'bg-white border border-[#71717A]'
                  }`} />
                </div>
                {/* Content Column */}
                <div className="flex flex-col items-start gap-1 flex-1">
                  <p className="text-[14px] font-medium leading-[20px] text-[#11181C]">
                    Answer Follow-up Questions & Submit
                  </p>
                  <p className="text-[14px] font-normal leading-[20px] text-[#71717A]">
                    Review and answer follow-up questions, and submit your final responses.
                  </p>
                </div>
              </div>
            </div>

            {/* Auto-save Note - only show for welcome and help modes */}
            {!isChatWelcomeMode && (
              <p className="text-[14px] font-normal leading-[20px] text-center text-[#3F3F46] w-full">
                Your progress will be saved automatically. You can return at any time.
              </p>
            )}

            {/* Action Button */}
            <ZButton
              className={`w-full h-10 px-4 py-2 text-[16px] font-medium leading-[24px] rounded-md ${
                isHelpMode
                  ? 'bg-white border border-[#006FEE] text-[#006FEE]'
                  : 'bg-[#006FEE] text-white'
              }`}
              onClick={onClose}
            >
              {buttonText}
            </ZButton>
          </div>
        </div>
      </ModalContent>
    </Modal>
  );
};

export default WelcomeModal;

